import { useEffect, type JSX } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { isVideoFileName } from './api';

// Fullscreen image viewer shared by the asset browser and ticket cards. Click
// the backdrop, the close button, or press Esc to close. When given more than
// one image, Left/Right arrows (or the on-screen chevrons) step through the set
// with wraparound. RemoveScroll locks background page scroll while open.
export function Lightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: string[];
  index: number;
  onIndexChange: (next: number) => void;
  onClose: () => void;
}): JSX.Element | null {
  const count = images.length;
  const url = images[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (count > 1 && e.key === 'ArrowRight') {
        e.preventDefault();
        onIndexChange((index + 1) % count);
      } else if (count > 1 && e.key === 'ArrowLeft') {
        e.preventDefault();
        onIndexChange((index - 1 + count) % count);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onIndexChange]);

  if (!url) return null;

  return (
    <RemoveScroll>
      <div className="lightbox" onClick={onClose}>
        {isVideoFileName(url) ? (
          <video src={url} controls autoPlay onClick={(e) => e.stopPropagation()} />
        ) : (
          <img src={url} alt="" onClick={(e) => e.stopPropagation()} />
        )}
        {count > 1 && (
          <>
            <button
              className="lightbox-nav prev"
              title="Previous (Left arrow)"
              onClick={(e) => {
                e.stopPropagation();
                onIndexChange((index - 1 + count) % count);
              }}
            >
              <i className="fas fa-chevron-left" />
            </button>
            <button
              className="lightbox-nav next"
              title="Next (Right arrow)"
              onClick={(e) => {
                e.stopPropagation();
                onIndexChange((index + 1) % count);
              }}
            >
              <i className="fas fa-chevron-right" />
            </button>
            <div className="lightbox-counter">
              {index + 1} / {count}
            </div>
          </>
        )}
        <button className="lightbox-close" onClick={onClose} title="Close">
          <i className="fas fa-times" />
        </button>
      </div>
    </RemoveScroll>
  );
}
